import React, { useContext } from 'react'

import VisibilityContext from 'src/provider/state-manager/visibilityProvider'
import { catchErrMsg, erroMessage, requestMessage } from 'src/utils/utility'

import apiCaller from '../../interceptor'

export interface SurveyTrigger {
    id: number
    triggerName: string
    triggerDescription: string
    surveyInterval: number
    createdAt: string
}

export interface SurveyQuestion {
    id: number
    questionText: string
    surveyType: string
    surveyTriggerID?: number
}

export interface SurveySummary {
    totalResponses: number
    averageRating: number
    totalRatingResponses: number
    totalPositiveResponses: number
    totalNegativeResponses: number
}

export interface SurveyResponseRow {
    id?: number
    surveyTriggerID: number
    profileID: number
    responses: string
}

export interface SurveyResponseListResult {
    data: SurveyResponseRow[]
    total: number
}

export interface SurveyReportFilter {
    surveyTriggerID?: string
    startDateTime?: string
    endDateTime?: string
    returnMax?: boolean
}

const SurveyApiContext = React.createContext<any>(null)

export const SurveyApiProvider = (props: any) => {

    const { loader, notifier } = useContext(VisibilityContext)

    async function getSurveyTriggers(ibInstitutionCode: number | string): Promise<SurveyTrigger[] | null | false> {
        try {
            loader(true)
            const { data } = await apiCaller.get(`/v2/admin/Survey/Triggers?IBInstitutionCode=${ibInstitutionCode}`)

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }

            return data?.data
        }
        catch (err) {
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
        finally {
            loader(false)
        }
    }

    // NOTE: v1's equivalent call passed these query params as a raw object
    // in axios's config-argument position instead of `{ params }`, so axios
    // silently dropped them — the institution/trigger/date filters never
    // reached the backend. Fixed here to actually send them.
    async function getInstitutionSurveySummary(
        ibInstitutionCode: number | string,
        filter: SurveyReportFilter = {},
    ): Promise<SurveySummary | null | false> {
        try {
            loader(true)
            const { data } = await apiCaller.get('/v2/admin/Survey/Summaries', {
                params: { ibInstitutionCode, ...filter },
            })

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }

            return data?.data
        }
        catch (err) {
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
        finally {
            loader(false)
        }
    }

    // Same fix as getInstitutionSurveySummary above.
    async function surveyResponse(
        pageNumber: number,
        pageSize: number,
        ibInstitutionCode: number | string,
        filter: SurveyReportFilter = {},
    ): Promise<SurveyResponseListResult | null | false> {
        try {
            loader(true)
            const { data } = await apiCaller.get(
                `/v2/admin/Survey/Responses?currentPageNumber=${pageNumber}&perPage=${pageSize}`,
                { params: { ibInstitutionCode, ...filter } },
            )

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }

            return data
        }
        catch (err) {
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
        finally {
            loader(false)
        }
    }

    async function getSurveyQuestions(ibInstitutionCode: number | string): Promise<SurveyQuestion[] | null | false> {
        try {
            loader(true)
            const { data } = await apiCaller.get(`/v2/admin/Survey/Questions?IBInstitutionCode=${ibInstitutionCode}`)

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }

            return data?.data
        }
        catch (err) {
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
        finally {
            loader(false)
        }
    }

    async function saveSurveyQuestion(payload: {
        id?: number
        questionText: string
        surveyType: number
        surveyTriggerID: number
        ibInstitutionCode: number | string
    }) {
        try {
            loader(true)
            const { data } = await apiCaller.post(
                `/v2/admin/Survey/Questions?IBInstitutionCode=${payload.ibInstitutionCode}`,
                payload,
            )

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }

            notifier.show(data.message || 'Survey Question Saved successfully', null, 'success')
            return data?.data
        }
        catch (err) {
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
        finally {
            loader(false)
        }
    }

    async function saveSurveyTrigger(payload: {
        id?: number
        surveyInterval: number
        triggerName: string
        triggerDescription: string
        ibInstitutionCode: number | string
    }) {
        try {
            loader(true)
            const { data } = await apiCaller.post(
                `/v2/admin/Survey/Triggers?IBInstitutionCode=${payload.ibInstitutionCode}`,
                payload,
            )

            if (!data.isSuccessful) {
                notifier.show(requestMessage(data))
                return false
            }

            notifier.show(data.message || 'Survey Trigger Saved successfully', null, 'success')
            return data?.data
        }
        catch (err) {
            notifier.show(erroMessage(catchErrMsg(err)))
            return null
        }
        finally {
            loader(false)
        }
    }

    return (
        <SurveyApiContext.Provider
            value={{
                getSurveyTriggers,
                getInstitutionSurveySummary,
                surveyResponse,
                getSurveyQuestions,
                saveSurveyQuestion,
                saveSurveyTrigger,
            }}
        >
            {props.children}
        </SurveyApiContext.Provider>
    )
}

export default SurveyApiContext
